import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { FormProvider, useForm } from 'react-hook-form';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import TrashIcon from '@wix/da-ds/pkg/Icons/16x16/Trashcan';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import yupResolver from '@wix/da-shared-react/pkg/utils/yupResolver';
import {
  Question,
  QuestionFieldName,
  QuestionFormFields,
  AnswerType,
} from '../../../../types';
import QuestionField from '../../../../fields/QuestionField';
import AnswerTypeField from '../../../../fields/AnswerTypeField';
import AnswerChoicesGroupField from '../../../../fields/AnswerChoicesGroupField';
import QuestionIsRequiredField from '../../../../fields/QuestionIsRequiredField';
import { useValidation } from '../../../../validation';

import s from './QuestionForm.scss';

interface Props {
  question?: Question;
  onSave: (question: Question) => void;
  onDiscard: () => void;
}

const QuestionForm: React.FC<Props> = ({ question, onSave, onDiscard }) => {
  const { t } = useTranslation();
  const validationSchema = useValidation();
  const formContext = useForm<QuestionFormFields>({
    mode: 'onSubmit',
    defaultValues: {
      [QuestionFieldName.QUESTION]: question?.questionBody,
      [QuestionFieldName.ANSWER_TYPE]:
        question?.answerType ?? AnswerType.FREE_TEXT,
      [QuestionFieldName.IS_REQUIRED]: question?.isRequired ?? false,
      [QuestionFieldName.ANSWER_CHOICES]: question?.answerChoices || [
        {
          answerText: '',
          price: undefined,
        },
      ],
    },
    resolver: yupResolver(validationSchema),
  });

  const answerType = formContext.watch(QuestionFieldName.ANSWER_TYPE);

  const questionId = question?.id ?? uuidv4();
  const onSaveClick = useCallback(() => {
    formContext.handleSubmit(data => {
      const questionBody = data.question && data.question();
      onSave({
        id: questionId,
        answerType: data[QuestionFieldName.ANSWER_TYPE],
        isRequired: data[QuestionFieldName.IS_REQUIRED],
        questionBody: {
          excerpt: '',
          html: {
            features:
              '[{"type":"TIPTAP_FEATURE","data":{"tiptap":true,"version":1}}]',
            type: 'tiptap',
            markup: questionBody,
          },
        },
        answerChoices: data[QuestionFieldName.ANSWER_CHOICES],
      });
    })();
  }, [formContext, onSave, questionId]);

  return (
    <FormProvider {...formContext}>
      <div className={s['root']}>
        <div className={s['header']}>
          <Heading className={s['heading']}>
            {question
              ? t('customExclusivePopup.questionnaire.form.heading.edit')
              : t('customExclusivePopup.questionnaire.form.heading.add')}
          </Heading>
          <TextButton
            variant="edit-purple"
            size="medium"
            icon={TrashIcon}
            onClick={onDiscard}
          >
            {t('common.discard')}
          </TextButton>
        </div>
        <hr className={s['divider']} />

        <div className={s['fields']}>
          <QuestionField />
          <AnswerTypeField />
        </div>

        {[AnswerType.SINGLE_CHOICE, AnswerType.MULTIPLE_CHOICE].includes(
          answerType
        ) && <AnswerChoicesGroupField answerType={answerType} />}

        <hr className={s['divider']} />
        <div className={s['footer']}>
          <QuestionIsRequiredField />
          <SolidButton size="small" variant="edit-purple" onClick={onSaveClick}>
            {t('customExclusivePopup.questionnaire.form.save')}
          </SolidButton>
        </div>
      </div>
    </FormProvider>
  );
};
QuestionForm.displayName = 'QuestionForm';

export default QuestionForm;
